"use client";

import { InfiniteHits, useInstantSearch } from "react-instantsearch";
import { InstantSearchNext } from "react-instantsearch-nextjs";
import { createInfiniteHitsSessionStorageCache } from "instantsearch.js/es/lib/infiniteHitsCache";
import simpleStateMapping from "instantsearch.js/es/lib/stateMappings/simple";
import AlgoliaFilters from "@components/search/algolia-filters";
import AlgoliaSearchResultItem from "@components/search/algolia-search-result-item";
import AlgoliaCurrentRefinements from "@components/search/algolia-current-refinements";
import AlgoliaResultsCount from "@components/search/algolia-results-count";
import AlgoliaSearchBox from "@components/search/algolia-search-box";
import { useAlgoliaClient } from "@/algolia/algolia-provider";
import { ALGOLIA_PRODUCTS_INDEX, searchQueryUrl } from "@/algolia/search-url";
import AlgoliaVirtualFilters from "@components/search/algolia-virtual-filters";
import AlgoliaConfigure from "@components/search/algolia-configure";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

const infiniteHitsCache = createInfiniteHitsSessionStorageCache();

const PRESERVED_URL_PARAMS = ["fromLogin", "fromRegister"];

const baseStateMapping = simpleStateMapping();
const safeStateMapping: typeof baseStateMapping = {
  ...baseStateMapping,
  routeToState: (routeState = {}) => {
    const filtered = Object.fromEntries(
      Object.entries(routeState as Record<string, unknown>).filter(
        ([, v]) => v !== null && typeof v === "object",
      ),
    ) as Parameters<typeof baseStateMapping.routeToState>[0];
    return baseStateMapping.routeToState(filtered);
  },
};

const preservingRouter = {
  push(url: string) {
    if (typeof window === "undefined") return;
    const target = new URL(url, window.location.href);
    const current = new URL(window.location.href);
    for (const key of PRESERVED_URL_PARAMS) {
      const value = current.searchParams.get(key);
      if (value !== null && !target.searchParams.has(key)) {
        target.searchParams.set(key, value);
      }
    }
    window.history.pushState({}, "", target.toString());
  },
};

function SearchResults() {
  const t = useTranslations("Products-list");
  const { results } = useInstantSearch();
  const currentHitsPerPage = results?.hitsPerPage;

  return (
    <InfiniteHits
      cache={infiniteHitsCache}
      showPrevious={false}
      hitComponent={AlgoliaSearchResultItem}
      className="mt-[40px]"
      classNames={{
        root: "w-full mt-5",
        list: `grid ${currentHitsPerPage === 30 ? "grid-cols-6 max-md:grid-cols-1" : "grid-cols-8 max-md:grid-cols-2"} gap-5 gap-y-16 max-md:gap-y-8 mb-10`,
        loadMore: "mx-auto flex underline",
        disabledLoadMore: "hidden",
      }}
      translations={{
        showMoreButtonText: t("more-results"),
      }}
    />
  );
}

export default function AlgoliaSearchResults() {
  const [mounted, setMounted] = useState(false);
  const router = useRouter();
  const searchClient = useAlgoliaClient();

  function navigateToResults(search: string) {
    router.push(searchQueryUrl(search));
  }

  useEffect(() => {
    setMounted(true);
    window.scrollTo(0, 0);
  }, []);

  if (!mounted) return null;

  return (
    <InstantSearchNext
      searchClient={searchClient}
      routing={{
        stateMapping: safeStateMapping,
        router: preservingRouter,
      }}
      future={{
        preserveSharedStateOnUnmount: true,
        persistHierarchicalRootCount: true,
      }}
      indexName={ALGOLIA_PRODUCTS_INDEX}
    >
      <section className="md:w-[50%] mx-auto">
        <AlgoliaSearchBox onSuggestionSelect={navigateToResults} />
      </section>

      <AlgoliaResultsCount />

      <div className="py-4 mb-8 flex justify-between items-center max-md:-mx-[1.5rem] max-md:px-[1.5rem] max-md:sticky max-md:top-[85px] max-md:z-10 max-md:bg-primary-white max-md:dark:bg-primary-black">
        <AlgoliaVirtualFilters />
        <AlgoliaFilters />
        <AlgoliaConfigure />
      </div>
      <section className="max-md:hidden">
        <AlgoliaCurrentRefinements />
      </section>
      <SearchResults />
    </InstantSearchNext>
  );
}
